<script setup lang="ts">
import { Coupon } from '@/@types/coupon'
import TicketCategorySelector from '@/components/shared/coupons/TicketCategorySelector.vue'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import CurrencyInput from '@/components/ui/currency-input.vue/CurrencyInput.vue'
import { Input } from '@/components/ui/input'

import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import LightLayout from '@/layouts/LightLayout.vue'
import { cn } from '@/lib/utils'
import { Head, Link, useForm } from '@inertiajs/vue3'

import { DateFormatter, DateValue, getLocalTimeZone, parseDate, today } from '@internationalized/date'
import { ArrowLeftCircle, CalendarIcon, Percent } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    coupon: Coupon
    tickets: {
        workshop: string
        tickets: { label: string; value: number }[]
    }[]
}>()

const form = useForm({
    category: props.coupon.category,
    code: props.coupon.code,
    comment: props.coupon.comment,
    percentage_value: String(props.coupon.percentage_value),
    value_cents: String(props.coupon.value_cents),
    start_date: props.coupon.start_date,
    end_date: props.coupon.end_date,
    min_value: String(props.coupon.min_value_cents),
    ticket_ids: props.coupon.ticket_ids,
    usage_limit: props.coupon.usage_limit,
    status: props.coupon.status,
})

const { locale } = useI18n()

const df = new DateFormatter(locale.value, {
    dateStyle: 'long',
})

const startDate = ref<DateValue>()
const endDate = ref<DateValue>()

watch([startDate, endDate], () => {
    if (startDate.value) {
        form.start_date = startDate.value.toString()
    }

    if (endDate.value) {
        form.end_date = endDate.value.toString()
    }
})

const currentJustificationLength = computed(() => form.comment.trim().length)

watch(
    () => form.category,
    () => {
        if (form.category === 'currency') {
            form.percentage_value = ''
        } else {
            form.value_cents = ''
        }
    },
    {
        immediate: true,
    },
)

onMounted(() => {
    if (props.coupon.start_date) {
        startDate.value = parseDate(props.coupon.start_date.split('T')[0])
    }

    if (props.coupon.end_date) {
        endDate.value = parseDate(props.coupon.end_date.split('T')[0])
    }
})
</script>

<template>
    <Head :title="$t('Edit coupon')" />

    <LightLayout>
        <main class="mx-auto max-w-3xl p-4">
            <section class="flex items-center gap-2">
                <Link :href="`/coupons/${coupon.id}`" aria-label="Back to order">
                    <ArrowLeftCircle class="h-8 w-8 text-slate-800" stroke-width="1.2" />
                </Link>

                <h1 class="text-2xl font-semibold text-gray-800">
                    {{ $t('Edit coupon') }}
                </h1>
            </section>

            <section class="mt-6 lg:mt-12">
                <form
                    @submit.prevent="
                        form
                            .transform((data) => ({
                                coupon: {
                                    ...data,
                                    value_cents: Math.round(parseFloat(String(data.value_cents).replace(/\./g, '').replace(',', '.')) * 100),
                                },
                            }))
                            .put(`/coupons/${coupon.id}`)
                    "
                    class="grid gap-x-8 gap-y-6 md:grid-cols-2"
                >
                    <div class="col-span-full flex items-center justify-between gap-2">
                        <RadioGroup class="flex" v-model="form.category">
                            <Label
                                :data-state="form.category === 'currency' ? 'checked' : 'unchecked'"
                                :class="'data-[state=checked]:border-primary group flex w-fit items-center gap-2 rounded-[10px] border border-gray-200 p-4'"
                            >
                                <div class="group-data-[state=checked]:text-primary">{{ $t('currency') }}</div>
                                <RadioGroupItem class="[&_svg]:fill-primary" value="currency" />
                            </Label>
                            <Label
                                :data-state="form.category === 'percentage' ? 'checked' : 'unchecked'"
                                :class="
                                    cn(
                                        'data-[state=checked]:border-primary group flex w-fit items-center gap-2 rounded-[10px] border border-gray-200 p-4',
                                    )
                                "
                            >
                                <div class="group-data-[state=checked]:text-primary">
                                    {{ $t('percentage') }}
                                </div>
                                <RadioGroupItem class="[&_svg]:fill-primary" value="percentage" />
                            </Label>
                        </RadioGroup>

                        <Switch
                            :defaultValue="form.status === 'active'"
                            @update:modelValue="
                                (value) => {
                                    form.status = value ? 'active' : 'disabled'
                                }
                            "
                        />
                    </div>

                    <div class="col-span-full flex flex-col gap-2">
                        <Label required for="code">{{ $t('Code') }}</Label>
                        <Input
                            v-model="form.code"
                            type="text"
                            id="code"
                            :disabled="coupon.usage_count > 0"
                            @input="
                                (e: Event) =>
                                    (form.code = (e.target as HTMLInputElement).value
                                        .toUpperCase()
                                        .replace(/[^A-Z0-9]/g, '')
                                        .trim())
                            "
                            maxlength="12"
                            placeholder="Ex. CUPOM10"
                        />

                        <p v-if="form.errors.code" class="text-destructive text-xs">{{ form.errors.code }}</p>
                    </div>

                    <div class="flex flex-col gap-1.5">
                        <Label required for="start_date">{{ $t('Start date') }}</Label>
                        <Popover>
                            <PopoverTrigger as-child>
                                <Button variant="outline" :class="cn('justify-between text-left font-normal', !startDate && 'text-muted-foreground')">
                                    {{ startDate ? df.format(startDate.toDate(getLocalTimeZone())) : $t('Select') }}
                                    <CalendarIcon class="mr-2 h-4 w-4" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent class="w-auto p-0">
                                <Calendar v-model="startDate" initial-focus :min-value="today(getLocalTimeZone())" />
                            </PopoverContent>
                        </Popover>
                        <p v-if="form.errors.start_date" class="text-destructive text-xs">{{ form.errors.start_date }}</p>
                    </div>
                    <div class="flex flex-col gap-1.5">
                        <Label required for="end_date">{{ $t('End date') }}</Label>
                        <Popover>
                            <PopoverTrigger as-child>
                                <Button variant="outline" :class="cn('justify-between text-left font-normal', !endDate && 'text-muted-foreground')">
                                    {{ endDate ? df.format(endDate.toDate(getLocalTimeZone())) : $t('Select') }}
                                    <CalendarIcon class="mr-2 h-4 w-4" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent class="w-auto p-0">
                                <Calendar v-model="endDate" initial-focus :min-value="today(getLocalTimeZone())" />
                            </PopoverContent>
                        </Popover>
                        <p v-if="form.errors.end_date" class="text-destructive text-xs">{{ form.errors.end_date }}</p>
                    </div>

                    <div class="flex flex-col gap-2">
                        <Label required for="value_cents">{{ $t('Discount amount') }}</Label>
                        <CurrencyInput v-model="form.value_cents" value="0" v-if="form.category === 'currency'" />
                        <div class="relative w-full items-center" v-if="form.category === 'percentage'">
                            <Input v-model="form.percentage_value" type="number" min="1" />
                            <span class="absolute inset-y-0 end-0 flex items-center justify-center px-2">
                                <Percent class="text-foreground size-6" />
                            </span>
                        </div>

                        <p v-if="form.errors.percentage_value" class="text-destructive text-xs">{{ form.errors.percentage_value }}</p>
                        <p v-if="form.errors.value_cents" class="text-destructive text-xs">{{ form.errors.value_cents }}</p>
                    </div>

                    <div class="flex flex-col gap-2">
                        <Label required for="value_cents">{{ $t('Maximum number of uses') }}</Label>
                        <Input v-model="form.usage_limit" type="number" min="0" />

                        <p v-if="form.errors.usage_limit" class="text-destructive text-xs">{{ form.errors.usage_limit }}</p>
                    </div>

                    <div class="col-span-full flex flex-col gap-2">
                        <Label required for="value_cents">Valor mínimo do carrinho</Label>
                        <CurrencyInput v-model="form.min_value" value="0" />

                        <p v-if="form.errors.min_value" class="text-destructive text-xs">{{ form.errors.min_value }}</p>
                    </div>

                    <TicketCategorySelector v-model="form.ticket_ids" :tickets="tickets" :error="form.errors.ticket_ids" />

                    <div class="col-span-full flex flex-col gap-2">
                        <Label for="comment">{{ $t('Justification') }}</Label>
                        <Textarea v-model="form.comment" type="text" maxlength="200" :placeholder="$t('Enter more information about the coupon')" />

                        <p v-if="form.errors.comment" class="text-destructive text-xs">{{ form.errors.comment }}</p>

                        <p class="text-sm text-slate-500">
                            <strong>{{ currentJustificationLength }}</strong
                            >/200 caracteres
                        </p>
                    </div>

                    <div class="mt-6 flex items-center gap-3">
                        <Button
                            :as="Link"
                            :href="`/coupons/${coupon.id}`"
                            variant="outline"
                            class="border-primary text-primary hover:bg-primary/5 hover:text-primary"
                            >{{ $t('Cancel') }}</Button
                        >
                        <Button>{{ $t('Edit coupon') }}</Button>
                    </div>
                </form>
            </section>
        </main>
    </LightLayout>
</template>
